// @ts-nocheck
import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2 } from "lucide-react";
import { CONSTRAINT_TYPE_LABELS } from "@/lib/constraints/humanize";
import { ConstraintCard, type ConstraintRow } from "./ConstraintCard";
import { ReviewConstraintDialog } from "./ReviewConstraintDialog";
import { AddOperatorConstraintDialog } from "./AddOperatorConstraintDialog";

interface Props {
  eventId: string;
  organizationId: string;
}

interface TeamOption {
  value: string;
  label: string;
}

const STATUS_COLUMNS = ["pending_review", "approved", "partial", "denied"] as const;
const STATUS_COLUMN_LABELS: Record<string, string> = {
  pending_review: "Pending",
  approved: "Approved",
  partial: "Partial",
  denied: "Denied",
};

export function EventScheduleRequestsTab({ eventId, organizationId }: Props) {
  // ── Data state ──
  const [constraints, setConstraints] = useState<ConstraintRow[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Filter state ──
  const [filteredTeamId, setFilteredTeamId] = useState<string>("__all__");
  const [filteredType, setFilteredType] = useState<string>("__all__");
  const [showDenied, setShowDenied] = useState(false);

  // ── Dialog state ──
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewingConstraint, setReviewingConstraint] = useState<ConstraintRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // ── Fetch constraints ──
  const fetchConstraints = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("get_event_constraints", {
      p_event_id: eventId,
      p_statuses: ["pending_review", "approved", "denied", "partial", "superseded"],
    });
    if (error) {
      console.error("Error fetching constraints:", error);
      toast.error("Failed to load schedule constraints");
    } else {
      setConstraints((data as ConstraintRow[]) || []);
    }
    setLoading(false);
  }, [eventId]);

  // ── Fetch teams (opponent candidates with null exclude = all teams) ──
  const fetchTeams = useCallback(async () => {
    const { data, error } = await supabase.rpc("get_event_opponent_candidates", {
      p_event_id: eventId,
      p_exclude_team_id: null,
    });
    if (error) {
      console.error("Error fetching teams:", error);
    } else {
      const opts: TeamOption[] = (data || []).map((t: any) => ({
        value: t.team_id,
        label: `${t.display_name}${t.age_group || t.gender ? ` — ${[t.age_group, t.gender].filter(Boolean).join(" ")}` : ""}`,
      }));
      setTeams(opts);
    }
  }, [eventId]);

  useEffect(() => {
    fetchConstraints();
    fetchTeams();
  }, [fetchConstraints, fetchTeams]);

  // ── Derived data ──

  const teamNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teams) {
      m.set(t.value, t.label);
    }
    return m;
  }, [teams]);

  const opponentNameLookup = useCallback(
    (teamId: string) => {
      const full = teamNameMap.get(teamId);
      if (!full) return undefined;
      // Strip the "— age gender" suffix for inline display
      const dash = full.indexOf(" — ");
      return dash > 0 ? full.substring(0, dash) : full;
    },
    [teamNameMap]
  );

  const filtered = useMemo(() => {
    return constraints.filter((c) => {
      if (filteredTeamId !== "__all__" && c.team_id !== filteredTeamId) return false;
      if (filteredType !== "__all__" && c.constraint_type !== filteredType) return false;
      return true;
    });
  }, [constraints, filteredTeamId, filteredType]);

  const grouped = useMemo(() => {
    const g: Record<string, ConstraintRow[]> = {
      pending_review: [],
      approved: [],
      partial: [],
      denied: [],
    };
    for (const c of filtered) {
      if (g[c.status]) {
        g[c.status].push(c);
      }
    }
    return g;
  }, [filtered]);

  const pendingCount = grouped.pending_review.length;

  // ── Handlers ──

  const handleCardClick = (c: ConstraintRow) => {
    setReviewingConstraint(c);
    setReviewOpen(true);
  };

  const handleMutationSuccess = () => {
    fetchConstraints();
  };

  // ── Visible columns ──
  const visibleColumns = showDenied
    ? STATUS_COLUMNS
    : STATUS_COLUMNS.filter((s) => s !== "denied");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm text-muted-foreground">Loading constraints...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold">Schedule Requests</h2>
          {pendingCount > 0 && (
            <Badge className="bg-red-100 text-red-800 border-red-200">
              {pendingCount} pending
            </Badge>
          )}
        </div>
        <Button onClick={() => setAddOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add constraint
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground whitespace-nowrap">Team</label>
          <Select value={filteredTeamId} onValueChange={setFilteredTeamId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All teams</SelectItem>
              {teams.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground whitespace-nowrap">Type</label>
          <Select value={filteredType} onValueChange={setFilteredType}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All types</SelectItem>
              {Object.entries(CONSTRAINT_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Switch checked={showDenied} onCheckedChange={setShowDenied} id="show-denied" />
          <label htmlFor="show-denied" className="text-sm text-muted-foreground cursor-pointer">
            Show denied
          </label>
        </div>
      </div>

      {/* Columns */}
      <div className={`grid gap-4 ${showDenied ? "grid-cols-4" : "grid-cols-3"}`}>
        {visibleColumns.map((status) => {
          const items = grouped[status] || [];
          return (
            <div key={status} className="space-y-2">
              <div className="flex items-center gap-2 pb-1 border-b">
                <h3 className="text-sm font-semibold">
                  {STATUS_COLUMN_LABELS[status]}
                </h3>
                <Badge variant="outline" className="text-xs">
                  {items.length}
                </Badge>
              </div>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                {items.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">None</p>
                ) : (
                  items.map((c) => (
                    <ConstraintCard
                      key={c.id}
                      constraint={c}
                      teamName={c.team_id ? (opponentNameLookup(c.team_id) || c.team_id) : null}
                      opponentNameLookup={opponentNameLookup}
                      onClick={handleCardClick}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialogs */}
      <ReviewConstraintDialog
        constraint={reviewingConstraint}
        teamName={
          reviewingConstraint?.team_id
            ? opponentNameLookup(reviewingConstraint.team_id) || reviewingConstraint.team_id
            : null
        }
        opponentNameLookup={opponentNameLookup}
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        onSuccess={handleMutationSuccess}
      />
      <AddOperatorConstraintDialog
        eventId={eventId}
        open={addOpen}
        onOpenChange={setAddOpen}
        onSuccess={handleMutationSuccess}
        teamsForFilter={teams}
        opponentOptions={teams}
      />
    </div>
  );
}